import React, { useState } from "react";
import { Sparkles, Check, RefreshCw, ChevronLeft } from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  AppBadge, AppButton, AppModal, AiModelPicker, Callout, Checkbox, FormField, HelpText, Input, Textarea, cn,
} from "../ui";
import useAiModels from "../../hooks/useAiModels";
import AiDialogSteps from "./AiDialogSteps";
import { QUESTION_TYPE_LABELS } from "./quizBankRules";

const DIFFICULTY_COLORS = { easy: "emerald", medium: "amber", hard: "rose" };

const MAX_QUESTIONS = 25;

// The submit button sits in the modal's footer, outside the form element.
const FORM_ID = "quiz-ai-questions-form";

/** The answer key of one generated question, in a line or two. */
function AnswerSummary({ q }) {
  if (q.options?.length) {
    return (
      <ul className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-1.5 tw-p-0">
        {q.options.map((o, i) => (
          <li
            key={i}
            className={cn(
              "tw-inline-flex tw-items-center tw-gap-1 tw-rounded-sm tw-border tw-border-solid tw-px-2 tw-py-0.5 tw-text-xs",
              o.isCorrect ? "tw-border-success/50 tw-bg-success/[0.08] tw-text-success-fg" : "tw-border-line tw-text-content-muted",
            )}
          >
            {o.isCorrect && <Check size={11} aria-hidden="true" />}
            {o.isCorrect && <span className="tw-sr-only">Correct: </span>}
            {o.text}
          </li>
        ))}
      </ul>
    );
  }
  if (q.acceptedAnswers?.length) {
    return <p className="tw-m-0 tw-text-xs tw-text-content-muted">Accepted: {q.acceptedAnswers.join(" · ")}</p>;
  }
  if (q.questionType === "numeric_answer") {
    return (
      <p className="tw-m-0 tw-text-xs tw-text-content-muted">
        Answer: {q.numericAnswer}
        {q.numericTolerance > 0 ? ` (± ${q.numericTolerance})` : ""}
      </p>
    );
  }
  return null;
}

/**
 * GenerateAiQuestionsModal — AI question authoring for one subtopic folder.
 * Opened from inside a subtopic: topic + subtopic come preselected, the admin
 * picks how many questions, optional concepts to cover, and the AI model
 * (Describe). Generated questions are shown for review (untick the weak ones)
 * and the keepers are saved into the subtopic via the bulk-upload endpoint
 * (Review).
 *
 * Both tenantadmin and institute admins pick from the same catalogue; an
 * institute generation draws from its admin AI token pool, which is what
 * `quota` reports.
 */
const GenerateAiQuestionsModal = ({ category, subcategory, onClose, onSaved }) => {
  const ai = useAiModels("/quiz-questions/ai-models");
  const { models, quota, enabled: aiEnabled } = ai;
  const [form, setForm] = useState({ count: 10, concepts: "" });
  const [step, setStep] = useState("form"); // form | review
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState({ questions: [], model: "" });
  const [picked, setPicked] = useState(() => new Set());

  const quotaBlockedMsg =
    quota && quota.allowed === false
      ? quota.reason === "quota_exhausted"
        ? "Your institute has used all its AI tokens for this month."
        : "AI generation is not available on your current plan."
      : "";

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    setError("");
    try {
      const { data } = await apiClient.post("/quiz-questions/generate-ai", {
        category: category._id,
        subcategory: subcategory._id,
        count: Math.min(Math.max(Number(form.count) || 10, 1), MAX_QUESTIONS),
        concepts: form.concepts.trim(),
        ...ai.payload,
      });
      // `data.model` is what RAN, not what was picked — the server falls back
      // when the account cannot reach a model.
      setResult({ questions: data.questions || [], model: data.model || ai.model });
      setPicked(new Set((data.questions || []).map((_, i) => i)));
      setStep("review");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to generate questions.");
    }
    setGenerating(false);
  };

  const togglePick = (i) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const handleSave = async () => {
    const keep = result.questions.filter((_, i) => picked.has(i));
    if (!keep.length) return;
    setSaving(true);
    setError("");
    try {
      const { data } = await apiClient.post("/quiz-questions/bulk-upload", {
        questions: keep.map((q) => ({
          ...q,
          category: category._id,
          subcategory: subcategory._id,
        })),
      });
      onSaved(data.message || `${keep.length} question(s) added.`);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save questions.");
      setSaving(false);
    }
  };

  const modelLabel = models.find((m) => m.id === result.model)?.label || result.model;

  const footer = step === "form" ? (
    <>
      <AppButton variant="outline" type="button" onClick={onClose} disabled={generating}>
        Cancel
      </AppButton>
      <AppButton
        variant="default"
        type="submit"
        form={FORM_ID}
        disabled={generating || !aiEnabled || !models.length || !!quotaBlockedMsg}
      >
        {generating
          ? <><RefreshCw size={14} className="tw-animate-spin" aria-hidden="true" /> Generating…</>
          : <><Sparkles size={14} aria-hidden="true" /> Generate</>}
      </AppButton>
    </>
  ) : (
    <>
      <AppButton variant="outline" type="button" onClick={onClose} disabled={saving}>
        Discard
      </AppButton>
      <AppButton variant="default" type="button" onClick={handleSave} disabled={saving || picked.size === 0}>
        {saving ? "Saving…" : `Save ${picked.size} question${picked.size === 1 ? "" : "s"}`}
      </AppButton>
    </>
  );

  return (
    <AppModal
      show
      onHide={() => !generating && !saving && onClose()}
      size="lg"
      icon={<Sparkles size={18} className="tw-align-[-3px] tw-text-chart-1" aria-hidden="true" />}
      title="Generate questions with AI"
      footer={footer}
    >
      <AiDialogSteps step={step} />

      {step === "form" ? (
        <form id={FORM_ID} onSubmit={handleGenerate} className="tw-flex tw-flex-col tw-gap-4">
          <p className="tw-m-0 tw-text-sm tw-text-content">
            New questions go into{" "}
            <strong>{category?.name}</strong>
            <span aria-hidden="true"> › </span>
            <span className="tw-sr-only">, subtopic </span>
            <strong>{subcategory?.name}</strong>.
          </p>

          <FormField label="Number of questions" hint={`Up to ${MAX_QUESTIONS} at a time.`}>
            <Input
              type="number"
              min="1"
              max={MAX_QUESTIONS}
              value={form.count}
              onChange={(e) => setForm((f) => ({ ...f, count: e.target.value }))}
              required
              className="tw-max-w-[10rem]"
            />
          </FormField>

          <FormField label="Concepts to cover" hint="Optional — leave blank and the AI picks the key concepts of this subtopic.">
            <Textarea
              rows={3}
              placeholder="e.g. joins, indexes, normalization"
              value={form.concepts}
              onChange={(e) => setForm((f) => ({ ...f, concepts: e.target.value }))}
              maxLength={1000}
            />
          </FormField>

          <AiModelPicker {...ai} />

          {quota && quota.allowed !== false && quota.limit > 0 && (
            <HelpText>
              Institute AI tokens: {quota.used.toLocaleString()} / {quota.limit.toLocaleString()} used
              {" · "}{quota.remaining.toLocaleString()} left this month
            </HelpText>
          )}

          <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">
            The AI mixes question types to fit the material (no Image MCQ) and skips questions that already
            exist in this subtopic. You review everything before it's saved.
          </p>

          {quotaBlockedMsg && <Callout tone="danger" role="alert">{quotaBlockedMsg}</Callout>}
          {error && <Callout tone="danger" role="alert">{error}</Callout>}
        </form>
      ) : (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-text-[0.8125rem] tw-text-content-muted">
            <span>
              {result.questions.length} generated by <strong className="tw-text-content">{modelLabel}</strong>
              {" · "}{picked.size} selected — untick any you do not want.
            </span>
            <AppButton variant="ghost" size="sm" type="button" onClick={() => setStep("form")}>
              <ChevronLeft size={14} aria-hidden="true" /> Change settings
            </AppButton>
          </div>

          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
            {result.questions.map((q, i) => (
              <li
                key={i}
                className={cn(
                  "tw-flex tw-items-start tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-3 tw-transition-opacity",
                  !picked.has(i) && "tw-opacity-55",
                )}
              >
                <Checkbox
                  checked={picked.has(i)}
                  onChange={() => togglePick(i)}
                  aria-label={`Keep question ${i + 1}`}
                  className="tw-mt-0.5"
                />
                <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-2">
                  <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                    <AppBadge size="sm" variant="secondary">{QUESTION_TYPE_LABELS[q.questionType] || q.questionType}</AppBadge>
                    {q.difficulty && (
                      <AppBadge size="sm" color={DIFFICULTY_COLORS[q.difficulty]} className="tw-capitalize">
                        {q.difficulty}
                      </AppBadge>
                    )}
                    {q.tags?.slice(0, 3).map((t) => (
                      <AppBadge key={t} size="sm" variant="outline">{t}</AppBadge>
                    ))}
                  </div>
                  <p className="tw-m-0 tw-text-sm tw-text-content">{q.questionText}</p>
                  {q.codeSnippet && (
                    <pre className="tw-m-0 tw-overflow-x-auto tw-rounded-sm tw-bg-muted tw-px-2.5 tw-py-2 tw-font-mono tw-text-xs tw-text-content">
                      {q.codeSnippet}
                    </pre>
                  )}
                  <AnswerSummary q={q} />
                  {q.explanation && <p className="tw-m-0 tw-text-xs tw-text-content-subtle">{q.explanation}</p>}
                </div>
              </li>
            ))}
          </ul>

          {error && <Callout tone="danger" role="alert">{error}</Callout>}
        </div>
      )}
    </AppModal>
  );
};

export default GenerateAiQuestionsModal;
